interface Props {
  userPct: number;
  onAlert?: (message: string) => void;
  threshold?: number;
  speakerCount?: number;  // Number of detected speakers
  onSwapSpeaker?: () => void;  // Callback to swap which speaker is "You"
}

export function TalkRatioBar({ userPct, threshold = 70, speakerCount = 1, onSwapSpeaker }: Props) {
  const getStatus = () => {
    if (userPct < 30) return { color: 'blue', bgColor: 'bg-blue-500', msg: 'Share more', textColor: 'text-blue-600' };
    if (userPct <= 60) return { color: 'green', bgColor: 'bg-green-500', msg: 'Good balance', textColor: 'text-green-600' };
    if (userPct <= threshold) return { color: 'yellow', bgColor: 'bg-yellow-500', msg: 'Caution', textColor: 'text-yellow-600' };
    return { color: 'red', bgColor: 'bg-red-500', msg: 'Let them talk', textColor: 'text-red-600' };
  };

  const status = getStatus();

  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center text-xs">
        <span className="text-gray-500 font-medium">Talk Ratio</span>
        <span className={`font-medium ${status.textColor}`}>{status.msg}</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex">
        <div
          className={`${status.bgColor} transition-all duration-300`}
          style={{ width: `${userPct}%` }}
        />
      </div>
      <div className="flex justify-between items-center text-xs text-gray-400">
        <span>You: {Math.round(userPct)}%</span>
        {speakerCount >= 2 && onSwapSpeaker && (
          <button
            onClick={onSwapSpeaker}
            className="px-1.5 py-0.5 text-xs bg-gray-100 hover:bg-gray-200 rounded text-gray-500 hover:text-gray-700 transition-colors"
            title="Swap who is 'You' vs 'Them'"
          >
            ⇄ Swap
          </button>
        )}
        <span>Them: {Math.round(100 - userPct)}%</span>
      </div>
    </div>
  );
}

// Helper function to calculate talk ratio from state
export function calculateTalkRatio(userTalkTimeMs: number, otherTalkTimeMs: number): number {
  const total = userTalkTimeMs + otherTalkTimeMs;
  if (total === 0) return 50; // Default to balanced when no data
  return Math.round((userTalkTimeMs / total) * 100);
}

// Helper function to update talk ratio from a transcript segment
export function updateTalkRatioFromSegment(
  currentState: { userTalkTimeMs: number; otherTalkTimeMs: number },
  durationMs: number,
  speaker: number // 0 = user, 1+ = others
): { userTalkTimeMs: number; otherTalkTimeMs: number } {
  if (speaker === 0) {
    return {
      ...currentState,
      userTalkTimeMs: currentState.userTalkTimeMs + durationMs,
    };
  } else {
    return {
      ...currentState,
      otherTalkTimeMs: currentState.otherTalkTimeMs + durationMs,
    };
  }
}
